import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Button, Select, message } from 'antd-v5';
import { SettingsContent } from '../../../components/SettingsContent';
import useVendorByKey from '../../../../../../hooks/query/useVendorByKey';
import { getVendorSettings, updateVendorSettings } from '../../../../../../api/vendors';

const DEFAULT_CURRENCY = 'USD';

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
];

export const PreferredCurrency = () => {
  const queryClient = useQueryClient();
  const { vendorId, isLoading: vendorLoading } = useVendorByKey('rolex');
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [savedCurrency, setSavedCurrency] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!vendorId) return;
    setLoading(true);
    setFetchError(false);
    getVendorSettings(vendorId)
      .then(response => {
        // getRequest resolves false on network/auth errors instead of throwing
        if (response === false) {
          setFetchError(true);
          return;
        }
        const value = response?.preferredCurrency?.toUpperCase();
        if (value) {
          setCurrency(value);
          setSavedCurrency(value);
        } else {
          setCurrency(DEFAULT_CURRENCY);
          setSavedCurrency(null);
        }
      })
      .catch(() => {
        setFetchError(true);
      })
      .finally(() => setLoading(false));
  }, [vendorId]);

  const handleSave = async () => {
    if (!vendorId) return;
    setSaving(true);
    try {
      await updateVendorSettings(vendorId, { preferredCurrency: currency });
      setSavedCurrency(currency);
      queryClient.invalidateQueries('getVendors');
      message.success('Preferred currency saved');
    } catch {
      message.error('Failed to save preferred currency');
    } finally {
      setSaving(false);
    }
  };

  const hasChanged = currency !== (savedCurrency || DEFAULT_CURRENCY);

  return (
    <SettingsContent title="Preferred Currency" description="Set the currency used for Rolex catalog pricing">
      <div className="flex-row items-center gap-8" style={{ maxWidth: 320 }}>
        <Select
          value={currency}
          onChange={setCurrency}
          options={CURRENCY_OPTIONS}
          style={{ width: 240 }}
          disabled={loading || vendorLoading || fetchError}
        />
        <Button
          type="primary"
          onClick={handleSave}
          loading={saving}
          disabled={!hasChanged || loading || vendorLoading || fetchError}
        >
          Save
        </Button>
      </div>
      {fetchError && (
        <p className="m-top-4 m-btm-0" style={{ color: '#ff4d4f', fontSize: 12 }}>
          Failed to load current setting. Please refresh and try again.
        </p>
      )}
    </SettingsContent>
  );
};
